<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="show"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div class="absolute inset-0 bg-black/50" @click="close"></div>
        <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4">
          <!-- 头部 -->
          <div class="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 class="text-lg font-semibold text-slate-900">待办详情</h3>
            <button @click="close" class="p-1.5 text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <!-- 内容 -->
          <div class="p-6 max-h-[72vh] overflow-y-auto">
            <div v-if="loading" class="flex items-center justify-center py-8">
              <svg class="w-6 h-6 animate-spin text-primary-500" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <template v-else-if="displayItem">
              <!-- 标签行 -->
              <div class="flex items-center gap-2 mb-4">
                <span :class="getPriorityBadgeClass(displayItem.priority)">{{ displayItem.priorityDesc || getPriorityLabel(displayItem.priority) }}</span>
                <span :class="getStatusBadgeClass(displayItem.status)">{{ displayItem.statusDesc || getStatusLabel(displayItem.status) }}</span>
                <span v-if="displayItem.overdue" class="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">逾期</span>
              </div>
              <!-- 标题 -->
              <h4 class="text-xl font-semibold text-slate-900 mb-2">{{ displayItem.title }}</h4>
              <!-- 描述 -->
              <p class="text-sm text-slate-600 mb-4">{{ displayItem.description || '暂无描述' }}</p>
              <!-- 信息列表 -->
              <div class="space-y-3">
                <div class="flex items-center gap-3 text-sm">
                  <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                  <span class="text-slate-600">到期时间：</span>
                  <span :class="['text-slate-800', displayItem.overdue ? 'text-red-600' : '']">
                    {{ formatDate(displayItem.dueDate) }}
                  </span>
                </div>
                <div class="flex items-center gap-3 text-sm">
                  <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span class="text-slate-600">创建时间：</span>
                  <span class="text-slate-800">{{ formatDateTime(displayItem.createTime) }}</span>
                </div>
                <div v-if="displayItem.completeTime" class="flex items-center gap-3 text-sm">
                  <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span class="text-slate-600">完成时间：</span>
                  <span class="text-slate-800">{{ formatDateTime(displayItem.completeTime) }}</span>
                </div>
              </div>

              <div class="mt-6 border-t border-slate-200 pt-5">
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center gap-2">
                    <h4 class="text-sm font-semibold text-slate-900">批注</h4>
                    <span class="text-xs text-slate-500">{{ annotations.length }} 条</span>
                  </div>
                  <button
                    type="button"
                    @click="openAnnotationDialog"
                    class="px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 inline-flex items-center gap-2"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                    </svg>
                    添加批注
                  </button>
                </div>

                <div v-if="annotations.length > 0" class="space-y-3 max-h-56 overflow-y-auto pr-1">
                  <div
                    v-for="annotation in annotations"
                    :key="annotation.id"
                    class="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3"
                  >
                    <div class="flex items-center justify-between gap-3 mb-2">
                      <span class="text-sm font-medium text-slate-800">{{ annotation.creatorName || '系统用户' }}</span>
                      <span class="text-xs text-slate-500 shrink-0">{{ formatDateTime(annotation.createTime) }}</span>
                    </div>
                    <p class="text-sm text-slate-700 whitespace-pre-wrap break-words">{{ annotation.content }}</p>
                  </div>
                </div>
                <div v-else class="rounded-lg border border-dashed border-slate-200 py-5 text-center text-sm text-slate-500">
                  暂无批注
                </div>
              </div>
            </template>
          </div>
          <!-- 底部按钮 -->
          <div v-if="displayItem && displayItem.status !== 'COMPLETED'" class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button
              @click="handleComplete"
              class="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              标记完成
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="showAnnotationDialog"
        class="fixed inset-0 z-[60] flex items-center justify-center p-4"
      >
        <div class="absolute inset-0 bg-black/40" @click="closeAnnotationDialog"></div>
        <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4">
          <div class="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 class="text-lg font-semibold text-slate-900">添加批注</h3>
            <button
              type="button"
              @click="closeAnnotationDialog"
              class="p-1.5 text-slate-400 hover:text-slate-600"
              :disabled="annotationSubmitting"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div class="p-6">
            <label class="block text-sm font-medium text-slate-700 mb-2">
              批注内容 <span class="text-red-500">*</span>
            </label>
            <textarea
              v-model="annotationContent"
              rows="5"
              maxlength="2000"
              class="form-textarea"
              placeholder="请输入批注内容"
              autofocus
            ></textarea>
            <div class="mt-2 flex justify-end">
              <span class="text-xs text-slate-500">{{ annotationContent.length }}/2000</span>
            </div>
          </div>
          <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button
              type="button"
              @click="closeAnnotationDialog"
              :disabled="annotationSubmitting"
              class="px-5 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50"
            >
              取消
            </button>
            <button
              type="button"
              @click="handleAddAnnotation"
              :disabled="annotationSubmitting || !annotationContent.trim()"
              class="px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              <svg v-if="annotationSubmitting" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              确认
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { todoApi } from '@/api/todo'
import { ElMessage } from 'element-plus'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  item: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close', 'complete'])

const loading = ref(false)
const annotationContent = ref('')
const annotationSubmitting = ref(false)
const showAnnotationDialog = ref(false)

// 用于显示的详情数据，优先使用 fetchDetail 获取的完整数据
const displayItem = computed(() => detailItem.value || props.item)
const annotations = computed(() => displayItem.value?.annotations || [])

// 优先级映射
const priorityMap = {
  1: { label: '低', class: 'px-2 py-0.5 bg-slate-100 text-slate-700 text-xs rounded-full' },
  2: { label: '中', class: 'px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full' },
  3: { label: '高', class: 'px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full' },
  4: { label: '紧急', class: 'px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full' }
}

// 状态映射
const statusMap = {
  'PENDING': { label: '待办', class: 'px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full' },
  'IN_PROGRESS': { label: '进行中', class: 'px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full' },
  'COMPLETED': { label: '已完成', class: 'px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full' },
  'OVERDUE': { label: '已逾期', class: 'px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full' },
  'cancelled': { label: '已取消', class: 'px-2 py-0.5 bg-slate-100 text-slate-700 text-xs rounded-full' }
}

// 获取优先级标签
function getPriorityLabel(priority) {
  return priorityMap[priority]?.label || '未知'
}

// 获取优先级徽章类
function getPriorityBadgeClass(priority) {
  return priorityMap[priority]?.class || priorityMap[2].class
}

// 获取状态标签
function getStatusLabel(status) {
  return statusMap[status]?.label || '未知'
}

// 获取状态徽章类
function getStatusBadgeClass(status) {
  return statusMap[status]?.class || statusMap['PENDING'].class
}

// 格式化日期
function formatDate(dateStr) {
  if (!dateStr) return '未设置'
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr.substring(0, 10)
  return datePart
}

// 格式化日期时间
function formatDateTime(dateStr) {
  if (!dateStr) return '-'
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr.substring(0, 10)
  const timePart = dateStr.includes('T') ? dateStr.split('T')[1]?.substring(0, 5) : ''
  return timePart ? `${datePart} ${timePart}` : datePart
}

// 关闭弹窗
function close() {
  closeAnnotationDialog()
  emit('close')
}

// 完成待办
async function handleComplete() {
  if (!props.item) return

  try {
    await todoApi.complete(props.item.id)
    ElMessage.success('已完成')
    emit('complete')
    close()
  } catch (error) {
    console.error('完成待办失败:', error)
    ElMessage.error(error.message || '操作失败')
  }
}

// 添加批注
async function handleAddAnnotation() {
  const todoId = displayItem.value?.id
  const content = annotationContent.value.trim()
  if (!todoId || !content) return

  annotationSubmitting.value = true
  try {
    await todoApi.addAnnotation(todoId, { content })
    annotationContent.value = ''
    showAnnotationDialog.value = false
    ElMessage.success('批注已添加')
    await fetchDetail()
  } catch (error) {
    console.error('添加批注失败:', error)
    ElMessage.error(error.message || '添加批注失败')
  } finally {
    annotationSubmitting.value = false
  }
}

function openAnnotationDialog() {
  annotationContent.value = ''
  showAnnotationDialog.value = true
}

function closeAnnotationDialog() {
  if (annotationSubmitting.value) return
  showAnnotationDialog.value = false
  annotationContent.value = ''
}

// 详情数据
const detailItem = ref(null)

// 加载详情数据
async function fetchDetail() {
  if (!props.item?.id) return

  loading.value = true
  try {
    const res = await todoApi.getDetail(props.item.id)
    if (res) {
      detailItem.value = res
    }
  } catch (error) {
    console.error('获取待办详情失败:', error)
    ElMessage.error('加载详情失败')
  } finally {
    loading.value = false
  }
}

// 监听弹窗显示
watch(() => props.show, (val) => {
  if (val && props.item?.id) {
    fetchDetail()
  } else if (!val) {
    detailItem.value = null
    closeAnnotationDialog()
  }
})
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.95);
}
</style>
